import { useId, useState, type KeyboardEvent } from 'react';

const MAX_TAGS = 10;
const TAG_RE = /^[a-z0-9가-힣 _.-]{1,30}$/;
/** Same normalisation as the BFF (server/domain/documents.ts normalizeTags). */
const normTag = (s: string) => s.normalize('NFC').trim().toLowerCase().replace(/\s+/g, ' ');
const validTag = (s: string) => TAG_RE.test(s);

/** Chip input for optional document tags (tech design 17.2): Enter or "," adds, Backspace removes the last chip. */
export function TagInput({
  value,
  onChange,
  suggestions = [],
  placeholder,
  invalidHint,
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
  invalidHint?: string;
}) {
  const [draft, setDraft] = useState('');
  const [bad, setBad] = useState(false);
  const listId = useId();
  const add = (raw: string) => {
    const parts = raw.split(',').map(normTag).filter(Boolean);
    if (!parts.length) return true;
    if (parts.some((p) => !validTag(p))) {
      setBad(true);
      return false;
    }
    const next = [...value];
    for (const p of parts) if (!next.includes(p) && next.length < MAX_TAGS) next.push(p);
    onChange(next);
    setDraft('');
    setBad(false);
    return true;
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
  };
  const open = suggestions.filter((s) => !value.includes(s));
  return (
    <div>
      <div
        className={`flex min-h-[38px] flex-wrap items-center gap-1 rounded-md border bg-white px-2 py-1 ${bad ? 'border-red-400' : 'border-slate-300'}`}
      >
        {value.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800"
          >
            #{t}
            <button
              type="button"
              aria-label={`remove ${t}`}
              className="text-emerald-600 hover:text-red-600"
              onClick={() => onChange(value.filter((x) => x !== t))}
            >
              ×
            </button>
          </span>
        ))}
        {value.length < MAX_TAGS && (
          <input
            className="min-w-[6rem] flex-1 border-0 p-0.5 text-sm outline-none"
            value={draft}
            list={listId}
            placeholder={value.length ? '' : placeholder}
            maxLength={60}
            onChange={(e) => {
              setDraft(e.target.value);
              setBad(false);
              // picking a datalist entry fires a plain change with the full value
              if (open.includes(e.target.value)) add(e.target.value);
            }}
            onKeyDown={onKey}
            onBlur={() => draft && add(draft)}
          />
        )}
        <datalist id={listId}>
          {open.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      </div>
      {bad && invalidHint && <p className="mt-1 text-xs text-red-600">{invalidHint}</p>}
    </div>
  );
}
